// Background sync engine: mirrors the provider mailbox into the browser-local
// IndexedDB cache (email_cache.js). Content flows provider -> browser only.
//
// Incremental strategy: date-based re-list (`after:`/receivedDateTime ge the
// last sync marker) with upsert dedupe by message id. Provider delta tokens
// (Graph $delta / Gmail historyId) are a possible later optimization; the
// date-based approach is robust (no cursor invalidation) at the cost of
// re-fetching a small overlap window.

import * as emailApi from './email_interface.js';
import * as cache from './email_cache.js';

const syncState = {
  status: 'idle', // 'idle' | 'syncing' | 'error'
  syncedThisRun: 0,
  error: null,
};

let running = false;

function getSyncState() {
  return { ...syncState };
}

// Pages the provider listing and upserts everything into the local cache.
// - horizonTsSec: unix seconds; messages older than this are not synced.
// - account: the active account's email address; cached rows are tagged with
//   it and sync markers are kept per account (provider message ids are only
//   unique per mailbox).
// - If a previous sync completed and the horizon hasn't widened, only
//   messages since the last sync are fetched (incremental).
// - shouldStop(): optional cooperative-cancel callback (checked per page).
// - onProgress(state): optional callback after each page.
async function syncMailbox({ horizonTsSec, account, onProgress = null, shouldStop = null } = {}) {
  if (!horizonTsSec) {
    throw new Error('horizonTsSec required');
  }
  if (!account) {
    throw new Error('account required');
  }
  if (running) {
    return getSyncState();
  }
  running = true;
  syncState.status = 'syncing';
  syncState.error = null;
  syncState.syncedThisRun = 0;

  const horizonKey = `horizonTsSec:${account}`;
  const lastSyncKey = `lastSyncAt:${account}`;

  try {
    await cache.requestPersistence();

    const storedHorizon = await cache.getMeta(horizonKey);
    const lastSyncAt = await cache.getMeta(lastSyncKey);
    // Widened horizon (earlier date) or no prior sync => full sync from the
    // horizon; otherwise incremental from the last sync marker.
    let sinceTsSec = horizonTsSec;
    if (lastSyncAt && storedHorizon !== null && horizonTsSec >= storedHorizon) {
      sinceTsSec = lastSyncAt;
    }

    let cursor = null;
    let stopped = false;
    do {
      if (shouldStop && shouldStop()) {
        stopped = true;
        break;
      }
      const page = await emailApi.listMessagesForSync(sinceTsSec, cursor);
      const entries = (page.messages || [])
        .map((m) => emailApi.cacheEntryForMessage(m))
        .filter(Boolean)
        .map((e) => ({ ...e, account }));
      await cache.saveMessages(entries);
      syncState.syncedThisRun += entries.length;
      if (onProgress) {
        onProgress(getSyncState());
      }
      cursor = page.nextCursor;
    } while (cursor);

    if (!stopped) {
      await cache.setMeta(lastSyncKey, Math.floor(Date.now() / 1000));
      await cache.setMeta(horizonKey, horizonTsSec);
    }
    syncState.status = 'idle';
  } catch (err) {
    syncState.status = 'error';
    syncState.error = err;
    console.error('email sync failed', err);
  } finally {
    running = false;
  }
  return getSyncState();
}

export {
  syncMailbox,
  getSyncState,
};
